export enum GanttView {
    dayView = 0,
    weekView
}

export interface IResource {
    key: string;
    name: string;
    data?: any;
}

export interface IAppointment {
    key: string;
    resourceKey: string;
    dateFrom: Date;
    dateTo: Date;
    data?: any;
}

export interface IGanttHeader {
    curDate: Moment;
    view: GanttView;
    upperBound: Moment;
    lowerBound: Moment;
    intervals: number;
    widgetParameters?: any;
    changedValuesFromHeader?: any; //LL: 19.08.19  scaling, cellDuration
    theme?: any;
    options?: any; // any other otions such as currentDate, intervals, start, finish
    onChange?: (data) => void;
}

export interface IGanttBarComponent extends React.ComponentClass<any> {
    options?: any; // any other otions such as color, hatch, etc.
    data?: any; // app specific data to be shown in the bar
    glob?: any;
    onClick?: (e, data) => void;
}

//LL: all 4 "?" set by EU 07.03.18
export interface IGantt {
    id?: string;
    dateFrom?: Date;
    dateTo?: Date;
    panelList?: IGanttPanel[];
}

export interface IGanttPanel {
    id: string;
    dataRows: IGanttRow[];
    leftMargin?: IGanttMargin;
    rightMargin?: IGanttMargin;
}

export interface IGanttMargin {
    id: string;
    panelID: string;
    marginData: IGanttMarginRowData[];
    options?: IGanttMarginOptions;
}

export interface IGanttMarginOptions {
    marginID: string;
    borders: boolean; // whether columns in margin should be divided by borders
}

export interface IGanttRow {
    id: string;
    ownId?: string; //LL: 26.12.18
    parentId?: string; //LL: 26.12.18
    panelID: string;
    style?: any; // style object that could relate directly with CSS className (styling of color, border, etc.)
    text?: string; // not sure what text is for a row?
    subRowList: IGanttRow[];
    rowData: IGanttBodyRowData;
}

export interface IGanttMarginRowData {
    rowID: string;
    rowID2?: string; //LL: 02.12.21: for uniq
    cells: IGanttMarginCell[];
    cells2?: IGanttMarginCell[]; //LL: 19.02.19: for linkId2
}

export interface IGanttMarginCell {
    data: string;
}

export interface IGanttBodyRowData {
    rowID: string;
    bars?: IGanttBar[];
    icons?: IGanttIcon[];
    texts?: IGanttText[];
    horzLines?: IGanttHorizontalLine[];
    vertLines?: IGanttVerticalLine[];
}

export interface IGanttDataElement {
    id: string;
    rowID: string;
    dateFrom?: Date;
    dateTo?: Date; // not used for texts and vertical lines
    dateFromInitial?: Date; //LL for SCORE when Buffers are used
    dateToInitial?: Date; //LL for SCORE when Buffers are used
    bufferBeforeMinutes?: number; //LL for SCORE
    bufferAfterMinutes?: number; //LL for SCORE
    towSpan?: number; //LL for SCORE
    zIndex?: number;
    toolTip?: string;
    style?: any; // React requires styles to be declared as objects
    height?: number; // percentage of the row it blenongs
}

export interface IGanttBar extends IGanttDataElement {
    subStyleList?: ISubStyleSpec[];
    textList?: IBarTextSpec[];
    iconList?: IBarIconSpec[];
    barType?: BarType;
    data?: any; // any optional data
    resLinkValues?: any; //LL: 21.12.18:   {} with keys same as in self.resourceDataSources
    rowID_2?: string; //LL: 24.12.18  temp when 2 resourceDataSources
}

export interface IGanttIcon extends IGanttDataElement {
    icon: string; // name of the icon. Might be SVG file, or ????
}

export interface IGanttText extends IGanttDataElement {
    text: string;
    font: IFontSpec;
}

export interface IGanttHorizontalLine extends IGanttDataElement {
    width: number; // thickness of a line
}

export interface IGanttVerticalLine extends IGanttDataElement {
    width: number; // thickness of a line
}

// all properties are optional. that means that they inherted form parent container
export interface IFontSpec {
    size?: number;
    family?: string;
    style?: any;
}

export interface ISubStyleSpec {
    dateFrom: Date;
    dateTo: Date;
    style: string;
}

export interface IBarTextSpec {
    position: Position;
    style?: any;
    text: string;
    priority?: number; // 0..9
}

export interface IBarIconSpec {
    position: Position;
    style?: any;
    icon: string;
    priority?: number; // 0..9
}

export enum Position {
    ABOVE_BEFORE_LEFT = 0,
    ABOVE_LEFT,
    ABOVE_CENTER,
    ABOVE_RIGHT,
    ABOVE_AFTER_RIGHT,
    INSIDE_BEFORE_LEFT,
    INSIDE_LEFT,
    INSIDE_CENTER,
    INSIDE_RIGHT,
    INSIDE_AFTER_RIGHT,
    BELOW_BEFORE_LEFT,
    BELOW_LEFT,
    BELOW_CENTER,
    BELOW_RIGHT,
    BELOW_AFTER_RIGHT
}

export enum BarType {
    COMMON = 0,
    IN_BETWEEN
}

export enum PatternCode {
    Solid = 0,
    DiagonalRight = 1,
    DiagonalRightWide = 2,
    DiagonalLeft = 3,
    DiagonalLeftWide = 4,

    HorizontalLines = 5,
    HorizontalLinesWide = 6,
    VerticalLines = 7,
    VerticalLinesWide = 8,

    Checkered = 9,
    CheckeredLarge = 10,

    DotsSmall = 11,
    DotsMedium = 12,
    DotsLarge = 13,

    CheckeredDiagonal = 14
}

//LL: 19.10.17   ( "none" -> "byTimeCellWidth" ?)
export enum scalingCodeDef {
    none = 0,
    stretch = 1,
    fit = 2
}
export const scalingValuesDef = {
    values: ['none', 'stretch', 'fit'],
    display: ['no scaling', 'stretch', 'fit in Container']
};

// TimeCellWidth = 3 is min (left border + left padding). Better 4, last has border right
export const minTimeCellWidth = 4;

//LL: 15.08.19: from flux/Store
export const periodSelectionOptions = {
    values: ['useSchedulerPeriodSelector', 'useSchedulerPeriodSelectorLimited', 'useGlobalPeriodFilter'],
    display: [
        'Use Gantt Period Selector',
        'Use Gantt Period Selector limited by Period Filter',
        'Use Period Filter Widget'
    ],
    defaultPeriodSelection: 'useSchedulerPeriodSelector'
};

//LL: 19.06.20
export const towSpanCalculation = {
    defaultCalculationMode: 'automatic',
    defaultTowSpanInPixels: 7 // 20%, 36/5
};

//LL: 30.09.21: only by theme - instead of setValueByPropsOrTheme
export function getValueByTheme(theme, _themeField) {
    let V;
    if (theme != null && theme[themeFields[_themeField]] != null) {
        V = theme[themeFields[_themeField]];
    }
    return V;
}

//LL: see theme/main.ts
export const themeFields = {
    themeSection_Main: 'widget-gantt',
    themeSection_SCORE: 'widget-gantt-score',
    themeSection_Standplan: 'widget-gantt-standplan', //LL: 06.06.20
    themeSection_Margins: 'widget-gantt-margins',
    themeSection_Slider: 'widget-gantt-slider', //LL: 14.08.19 with previously created fields
    themeSection_Bars: 'widget-gantt-bars', //LL: 14.08.19 with previously created fields
    themeSection_Header: 'widget-gantt-header', //LL: 15.08.19  with new fields
    themeSection_DataRetrieving: 'widget-gantt-dataRetrieving', //LL: 15.08.19  with new fields
    themeSection_TimeLine: 'widget-gantt-timeLine', //LL: 16.08.19  with new fields

    rowHeight: 'widget-gantt-row-height',
    ganttBackgroundColor: 'widget-gantt-ganttBackgroundColor', //LL: 06.11.24
    ganttFontColor: 'widget-gantt-ganttFontColor', //LL: 12.11.24
    ganttControlsBackgroundColor: 'widget-gantt-ganttControlsBackgroundColor', //LL: 06.11.24
    ganttControlsBackgroundColor_selected: 'widget-gantt-ganttControlsBackgroundColor-selected', //LL: 06.11.24
    ganttControlsFontColor: 'widget-gantt-ganttControlsFontColor', //LL: 06.11.24
    vertTimeSeparatorLineColor: 'widget-gantt-vertTimeSeparatorLineColor', //LL: 10.01.25
    elementsBorderColor: 'widget-gantt-elementsBorderColor',
    horizLineColor: 'widget-gantt-horizLineColor',
    showHorizLines: 'widget-gantt-showHorizLines',
    todayLineColor: 'widget-gantt-todayLineColor', //LL: 22.11.19
    todayTimeZoneLineColor: 'widget-gantt-todayTimeZoneLineColor', //LL: 22.11.19
    showTodayPointersPane: 'widget-gantt-showTodayPointersPane', //LL: 01.12.19

    // DataRetrieving  2 groups  //LL: 15.08.19  with new fields
    themeGroup_DataRetrieving: 'widget-gantt-group-dataRetrieving',
    swapStartEnd: 'widget-gantt-dataRetrieving-swapStartEnd', //LL: 29.09.21
    hideEmptyResources: 'widget-gantt-dataRetrieving-hideEmptyResources',
    filterBarsByGroups: 'widget-gantt-dataRetrieving-filterBarsByGroups',
    retrieveGroupsWhenDateIsChanged: 'widget-gantt-dataRetrieving-retrieveGroupsWhenDateIsChanged',

    themeGroup_PeriodSelecton: 'widget-gantt-group-periodSelecton',
    retrieveDataFromBackendMode: 'widget-gantt-periodSelecton-retrieveDataFromBackendMode',
    initialDateToday: 'widget-gantt-periodSelecton-initialDateToday',
    initialDate: 'widget-gantt-periodSelecton-initialDate',

    // TimeLine 3 groups  //LL: 16.08.19  with new fields   4 groups
    themeGroup_TimeLine: 'widget-gantt-group-timeLine',
    minDate: 'widget-gantt-timeLine-minDate',
    maxDate: 'widget-gantt-timeLine-maxDate',
    cellDuration: 'widget-gantt-timeLine-cellDuration',

    themeGroup_ZoomBox: 'widget-gantt-group-zoomBox',
    showZoomBox: 'widget-gantt-zoomBox-showZoomBox',
    zoomValues: 'widget-gantt-zoomBox-zoomValues',

    themeGroup_Scaling: 'widget-gantt-group-scaling',
    showStretch: 'widget-gantt-scaling-showStretch', //  = 'Show Scaling in Header Panel'
    scaling: 'widget-gantt-scaling-scaling',
    timeCellWidth: 'widget-gantt-scaling-timeCellWidth',
    minTimeCellWidthForFit: 'widget-gantt-scaling-minTimeCellWidthForFit',

    //LL: 25.01.23: DST
    themeGroup_DST: 'widget-gantt-group-dst',
    dst_switchingDays: 'widget-gantt-dst-switchingDays',
    dst_obtainMethod: 'widget-gantt-dst-obtainMethod', //LL: 31.01.23

    // Header
    showGanttHeader: 'widget-gantt-header-showGanttHeader', //LL: 15.08.19  new field
    llHeaderHeight: 'widget-gantt-header-llHeaderHeight', //LL: 15.08.19  new field "Header Height(px) - temp"

    // Bars
    defaultBarBackcolor: 'widget-gantt-default-bar-backcolor',
    defaultBarTextcolor: 'widget-gantt-default-bar-textcolor', //LL: 02.09.21
    barCursor: 'widget-gantt-barCursor',
    appointmentLayout: 'widget-gantt-appointmentLayout', // was prop appointmentLayout, actually it is barLayout

    //  Slider
    sliderColor: 'widget-gantt-sliderColor',
    sliderDisabledColor: 'widget-gantt-sliderDisabledColor',

    //  Margins:  //LL: 20.11.18
    leftMarginBackcolor: 'widget-gantt-left-margin-backcolor',
    leftMarginWidth: 'widget-gantt-left-margin-width',
    rightMarginWidth: 'widget-gantt-right-margin-width',
    showSeparatoryLinesInMargins: 'widget-gantt-showSeparatoryLinesInMargins', //LL: 20.11.18
    textAlignInMargins: 'widget-gantt-textAlignInMargins', //LL: 20.11.18

    //  SCORE:
    sas_barBackColor: 'widget-gantt-sas-barBackColor',
    sas_barForeColor: 'widget-gantt-sas-barForeColor',
    sas_barPattern: 'widget-gantt-sas-barPattern',
    sas_textColor: 'widget-gantt-sas-textColor',
    sas_textOutsideColor: 'widget-gantt-sas-textOutsideColor',
    sas_bufferColor: 'widget-gantt-sas-bufferColor',
    sas_bufferTimeBefore: 'widget-gantt-sas-bufferTimeBefore',
    sas_bufferTimeAfter: 'widget-gantt-sas-bufferTimeAfter',
    sas_hideBuffers: 'widget-gantt-sas-hideBuffers',
    sas_towSpan: 'widget-gantt-sas-towSpan',
    sas_hasTowBefore: 'widget-gantt-sas-hasTowBefore',
    sas_hasTowAfter: 'widget-gantt-sas-hasTowAfter',

    //LL: 06.06.20:  Standplan
    standplan_barBackColor: 'widget-gantt-standplan-barBackColor',
    standplan_textColor: 'widget-gantt-standplan-textColor',
    standplan_borderColor: 'widget-gantt-standplan-borderColor', // new
    standplan_stripWidth: 'widget-gantt-standplan-stripWidth', // new
    standplan_towSpanCalculationMode: 'widget-gantt-standplan-towSpanCalculationMode',
    standplan_towSpan: 'widget-gantt-standplan-towSpan',
    standplan_towSpan_inPixels: 'widget-gantt-standplan-towSpan_inPixels',
    standplan_hasTowBefore: 'widget-gantt-standplan-hasTowBefore',
    standplan_hasTowAfter: 'widget-gantt-standplan-hasTowAfter'
};

//LL: 06.12.23: experiment for AuxRow
//export var tryAuxRow = true;
export var tryAuxRow = false;
